import React, { useEffect } from 'react';
import { Tabs, Tab, Box, IconButton, Modal, DialogContent, Divider, Typography } from '@mui/material';
import { PieChartComponent } from '../PieChart';
import { Close, Fullscreen } from '@mui/icons-material'; // Import Lock icon
import { IResultChart } from '../../core/interfaces/result.interface';
import BNGTabs from './bng-tabs';
import { IBiodiversityUnit } from '../../core/interfaces/BNG-units.interface';
import { Res } from '../../resources';

interface ChartTabsProps {
  chartData: Array<IResultChart & { type: 'chart' | 'bng' }>; // Add type property
  units: IBiodiversityUnit;
  productType: string;
  project?: any;
}

const ChartTabs: React.FC<ChartTabsProps> = ({ chartData, units, productType, project }) => {
  const [selectedTabIndex, setSelectedTabIndex] = React.useState(0);
  const hasProjectBNG =
    !!project &&
    (project.habitatUnits != null ||
      project.hedgerowUnits != null ||
      project.watercourseUnits != null ||
      project.totalHedgerowLength != null ||
      project.totalWatercourseLength != null);
  const hasUnits =
    !!units &&
    (units.habitatUnits != null ||
      units.hedgerowUnits != null ||
      units.watercourseUnits != null ||
      units.totalHedgerowLength != null ||
      units.totalWatercourseLength != null);
  const shouldShowBNG = hasProjectBNG || hasUnits;

  const [selectedTabData, setSelectedTabData] = React.useState(
    shouldShowBNG ? undefined : chartData[selectedTabIndex],
  );
  const [viewBNGUnits, setViewBNGUnits] = React.useState(shouldShowBNG);
  const [showChartModal, setShowChartModal] = React.useState(false);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTabIndex(newValue);
  };
  const normalizedProductType = (productType || '').toLowerCase();
  const isTrueSight =
    normalizedProductType === Res.product_names.INSTANT_HABMAP.toLowerCase() ||
    normalizedProductType === Res.product_names.TRUE_SIGHT.toLowerCase() ||
    normalizedProductType === Res.product_names.TRUE_SAGE.toLowerCase() ||
    normalizedProductType.includes('true sage') ||
    normalizedProductType.includes('true sight') ||
    normalizedProductType.includes('instant habmap');

  useEffect(() => {
    if (shouldShowBNG) {
      if (isTrueSight) {
        if (selectedTabIndex === 0) {
          setViewBNGUnits(true);
          setSelectedTabData(undefined);
        } else {
          setViewBNGUnits(false);
          setSelectedTabData(chartData[selectedTabIndex - 1]);
        }
      } else {
        setViewBNGUnits(false);
        setSelectedTabData(chartData[selectedTabIndex]);
      }
    } else {
      setViewBNGUnits(false);
      setSelectedTabData(chartData[selectedTabIndex]);
    }
  }, [selectedTabIndex, chartData, shouldShowBNG, isTrueSight]);

  return (
    <Box sx={{ width: '100%' }}>
      <Tabs value={selectedTabIndex} onChange={handleTabChange} variant="scrollable">
        {shouldShowBNG && isTrueSight && <Tab label="BNG Units" key="chart-tab_tab-panel_BNG" />}
        {chartData.map((tab) => (
          <Tab label={tab.displayName} key={`chart-tab_tab-panel_${tab.displayName}`} />
        ))}
      </Tabs>
      {viewBNGUnits && isTrueSight && <BNGTabs units={units} project={project} />}
      {selectedTabData && (
        <Box sx={{ display: 'flex', flexDirection: 'column', position: 'relative', py: 1 }}>
          <Box
            sx={{
              width: '100%',
              justifyContent: 'flex-end',
              display: 'flex',
              position: 'absolute',
              top: '4px',
              right: '4px',
            }}
          >
            {selectedTabData.type === 'chart' && (
              <IconButton
                onClick={() => {
                  setShowChartModal(true);
                }}
              >
                <Fullscreen />
              </IconButton>
            )}
          </Box>
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              height: selectedTabData.type === 'bng' ? '20vh' : 'auto', // Increase height for BNG tabs
            }}
          >
            <Box sx={{ width: '128px', height: '128px' }}>
              <PieChartComponent chart={selectedTabData} />
            </Box>
          </Box>
        </Box>
      )}
      <Modal
        open={showChartModal}
        onClose={() => {
          setShowChartModal(false);
        }}
      >
        <DialogContent
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '70vw',
            height: '70vh',
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 2,
            outline: 'none',
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
              overflow: 'hidden',
            }}
          >
            <Typography variant="h6">{selectedTabData?.displayName}</Typography>
            <IconButton
              onClick={() => {
                setShowChartModal(false);
              }}
            >
              <Close />
            </IconButton>
          </Box>
          <Divider sx={{ mb: 2, mt: 2 }} />
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              minHeight: '80%',
              alignItems: 'center',
            }}
          >
            {selectedTabData?.type === 'chart' ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  height: '55vh',
                  width: '100%',
                  overflow: 'hidden',
                }}
              >
                <PieChartComponent chart={selectedTabData} displayLegend />
              </Box>
            ) : (
              <></>
            )}
          </Box>
        </DialogContent>
      </Modal>
    </Box>
  );
};

export default ChartTabs;
